import { ThreadChat } from '@src/api/shared/community/chatApiClient';
import { ConfirmMessageDeleteModal } from '@src/features/shared/community/components/ConfirmMessageDeleteModal';
import ThreadHeader from '@src/features/shared/community/components/thread/ThreadHeader';
import ThreadInput from '@src/features/shared/community/components/thread/ThreadInput';
import ThreadParentPost from '@src/features/shared/community/components/thread/ThreadParentPost';
import ThreadReplyList from '@src/features/shared/community/components/thread/ThreadReplyList';
import ThreadSeparator from '@src/features/shared/community/components/thread/ThreadSeparator';
import ThreadSplitter from '@src/features/shared/community/components/thread/ThreadSplitter';
import { ThreadProvider, useThread } from '@src/features/shared/community/context/ThreadContext';
import { useFileDragDrop } from '@src/features/shared/community/hooks/useFileDragDrop';
import { useThreadHandlers } from '@src/features/shared/community/hooks/useThreadHandlers';
import { deleteThreadDraft } from '@src/idb';
import { TiptapEditorHandle } from '@src/ui/Editor';
import { useEffect, useRef, useState } from 'react';

import { AnonymousPost } from '../../enums/anonymousPost';
import { FileAttachment } from '../../enums/fileAttachment';
import FileUploadOverlay from '../chat/FileUploadOverlay';

interface ThreadPanelDesktopProps {
  parentChat?: ThreadChat;
  width: number;
  onWidthChange: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
}

const ThreadPanelDesktop: React.FC<ThreadPanelDesktopProps> = ({
  parentChat,
  width,
  onWidthChange,
  minWidth,
  maxWidth,
}) => {
  const tiptapEditorRef = useRef<TiptapEditorHandle>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const threadContextValue = useThread();
  const {
    isThreadOpen,
    setIsThreadOpen,
    activeThreadParentId,
    setParentChat,
    setThreadReplies,
    threadReplies,
    channelMembers,
    pinnedChat,
    linkPreviewCache,
    isLoggedIn,
    currentUserId,
    currentUserType,
    threadEditingChatId,
    setThreadEditingChatId,
    threadEditingMessage,
    setThreadEditingMessage,
    threadEditingAttachments,
    setThreadEditingAttachments,
    threadCreatingMessage,
    setThreadCreatingMessage,
    threadCreatingAttachments,
    setThreadCreatingAttachments,
    threadIsAnonymous,
    setThreadIsAnonymous,
    channel,
    isActionMenuSpOpen,
    currentChatIdSp,
    viewportHeight,
    firstUnreadThreadChatId,
    setInitialThreadChatId,
    onChatEditClick,
    onChatDeleteClick,
    onChatReportClick,
    onSetCurrentChatIdSp,
    onPinnedChatMenuClose,
    onPinnedChatLongPress,
    onToggleLinkPreview,
    onPinClick,
    getChatMenuPermission,
  } = threadContextValue;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingChat, setDeletingChat] = useState<ThreadChat | null>(null);
  const prevReplyCountRef = useRef<number>(threadReplies.length);
  const hasScrolledToUnreadRef = useRef<boolean>(false);

  // スレッドが開かれた時に未読位置または最下部へスクロール
  useEffect(() => {
    if (!isThreadOpen || !activeThreadParentId) {
      return;
    }

    // 新しくスレッドを開いた時の処理
    if (threadReplies.length > 0 && !hasScrolledToUnreadRef.current) {
      // 未読チャットがある場合は未読位置までスクロール
      if (firstUnreadThreadChatId && setInitialThreadChatId) {
        setInitialThreadChatId(firstUnreadThreadChatId);
        hasScrolledToUnreadRef.current = true;
      } else if (scrollContainerRef.current) {
        // 未読がない場合は最下部へスクロール
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'auto',
        });
        hasScrolledToUnreadRef.current = true;
      }
    }
  }, [
    isThreadOpen,
    activeThreadParentId,
    threadReplies.length,
    firstUnreadThreadChatId,
    setInitialThreadChatId,
  ]);

  // 返信が追加されたときに最下部へスクロール
  useEffect(() => {
    if (isThreadOpen && activeThreadParentId && hasScrolledToUnreadRef.current) {
      const hasNewReplies = threadReplies.length > prevReplyCountRef.current;

      if (hasNewReplies && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'auto',
        });
      }
      prevReplyCountRef.current = threadReplies.length;
    }
  }, [isThreadOpen, activeThreadParentId, threadReplies.length]);

  // スレッドが閉じられた時にリセット
  useEffect(() => {
    if (!isThreadOpen) {
      hasScrolledToUnreadRef.current = false;
      prevReplyCountRef.current = 0;
    }
  }, [isThreadOpen]);

  const { isDragging: isThreadDragging, dragHandlers } = useFileDragDrop({
    onFilesDropped: (files) => {
      tiptapEditorRef.current?.selectFiles(files);
    },
    isEnabled: isLoggedIn && channel?.allow_file_attachment === FileAttachment.ENABLED,
    onError: (message) => {
      tiptapEditorRef.current?.setAttachmentError(message);
    },
  });

  const { handleThreadReplySent, handleCloseThread: handleCloseThreadBase } = useThreadHandlers({
    channel,
    activeThreadParentId,
    parentChat,
    threadReplies,
    setParentChat: (parentChat: ThreadChat | undefined) => {
      if (setParentChat && parentChat) {
        setParentChat(parentChat);
      }
    },
    setThreadReplies,
    setThreadEditingChatId,
    setThreadEditingMessage,
    setThreadEditingAttachments,
    setThreadIsAnonymous,
    setActiveThreadParentId: () => {}, // ThreadPanelDesktopでは使用しないがインターフェースで必要
    setIsThreadOpen,
  });

  const handleSendClick = async () => {
    if (threadEditingChatId !== null) {
      setThreadEditingChatId(null);
      setThreadEditingMessage('');
      setThreadEditingAttachments([]);
    } else {
      if (activeThreadParentId && currentUserType && currentUserId && channel?.code) {
        await deleteThreadDraft(channel.code, activeThreadParentId, currentUserType, currentUserId);
      }
      setThreadCreatingMessage('');
      setThreadCreatingAttachments([]);
    }

    await handleThreadReplySent();
  };

  if (!isThreadOpen) {
    return null;
  }

  const handleCancelEdit = () => {
    setThreadEditingChatId(null);
    setThreadEditingMessage('');
    setThreadEditingAttachments([]);
  };

  const handleCloseThread = () => {
    handleCloseThreadBase();
  };

  const handleChatDeleteClick = (chat: ThreadChat) => {
    setDeletingChat(chat);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingChat) {
      onChatDeleteClick(deletingChat);
    }
    setIsDeleteModalOpen(false);
    setDeletingChat(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingChat(null);
  };

  const modifiedThreadContext = {
    ...threadContextValue,
    onChatDeleteClick: handleChatDeleteClick,
  };

  return (
    <ThreadProvider value={modifiedThreadContext}>
      <ThreadSplitter
        onWidthChange={onWidthChange}
        minWidth={minWidth}
        maxWidth={maxWidth}
      />

      <div
        className='relative flex flex-col bg-white'
        style={{ width: `${width}px` }}
        {...dragHandlers}
      >
        <ThreadHeader
          channelName={channel?.name ?? ''}
          onClose={handleCloseThread}
        />

        <div className='flex flex-1 flex-col overflow-hidden'>
          <div
            ref={scrollContainerRef}
            className='flex-1 overflow-y-auto'
          >
            {parentChat && (
              <>
                <ThreadParentPost
                  chat={parentChat}
                  channel={channel}
                  pinnedChat={pinnedChat}
                  linkPreviewCache={linkPreviewCache}
                  isLoggedIn={isLoggedIn}
                  currentUserId={currentUserId}
                  currentUserType={currentUserType}
                  editingChatId={threadEditingChatId}
                  isActionMenuSpOpen={isActionMenuSpOpen}
                  currentChatIdSp={currentChatIdSp}
                  onChatEditClick={onChatEditClick}
                  onChatDeleteClick={handleChatDeleteClick}
                  onChatReportClick={onChatReportClick}
                  onSetCurrentChatIdSp={onSetCurrentChatIdSp}
                  onPinnedChatMenuClose={onPinnedChatMenuClose}
                  onPinnedChatLongPress={onPinnedChatLongPress}
                  onToggleLinkPreview={onToggleLinkPreview}
                  onPinClick={onPinClick}
                  getChatMenuPermission={getChatMenuPermission}
                  chatRef={() => {}}
                />

                <ThreadSeparator
                  replyCount={threadReplies.length}
                  className='px-3'
                />

                <ThreadReplyList scrollContainerRef={scrollContainerRef} />
              </>
            )}
          </div>

          {channel?.can_post && isLoggedIn && (
            <ThreadInput
              ref={tiptapEditorRef}
              channelCode={channel.code}
              channelName={channel.name}
              channelMembers={channelMembers}
              channelVisibility={channel.visibility}
              message={threadEditingChatId !== null ? threadEditingMessage : threadCreatingMessage}
              attachments={
                threadEditingChatId !== null ? threadEditingAttachments : threadCreatingAttachments
              }
              isAnonymous={threadIsAnonymous}
              isEditing={threadEditingChatId !== null}
              editedChatId={threadEditingChatId ?? undefined}
              allowFileAttachment={channel.allow_file_attachment === FileAttachment.ENABLED}
              allowAnonymousPost={channel.allow_anonymous_post === AnonymousPost.ENABLED}
              viewportHeight={viewportHeight}
              isInitFocus={true}
              isLoggedIn={isLoggedIn}
              parentChatId={activeThreadParentId ?? undefined}
              onMessageChange={
                threadEditingChatId !== null ? setThreadEditingMessage : setThreadCreatingMessage
              }
              onAttachmentChange={
                threadEditingChatId !== null
                  ? setThreadEditingAttachments
                  : setThreadCreatingAttachments
              }
              onChangeIsAnonymous={setThreadIsAnonymous}
              onSend={handleSendClick}
              onCancelEdit={handleCancelEdit}
            />
          )}
        </div>

        {/* Thread drag overlay */}
        <FileUploadOverlay
          condition={isThreadDragging}
          text='アップロードする'
          className='pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-black/30'
          context='thread'
        />
      </div>

      {isDeleteModalOpen && (
        <ConfirmMessageDeleteModal
          onConfirm={handleConfirmDelete}
          onClose={handleCloseDeleteModal}
        />
      )}
    </ThreadProvider>
  );
};

export default ThreadPanelDesktop;
